/* @refresh skip */
import { useEffect, useState, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface UseVoiceInterfaceOptions {
  onTranscription?: (text: string) => void;
  onResponse?: (text: string) => void;
}

function extractFirstJsonObject(input: string): string | null {
  const s = input.trim();
  const start = s.indexOf("{");
  if (start === -1) return null;

  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let i = start; i < s.length; i++) {
    const ch = s[i];

    if (inString) {
      if (escaped) {
        escaped = false;
        continue;
      }
      if (ch === "\\") {
        escaped = true;
        continue;
      }
      if (ch === '"') {
        inString = false;
      }
      continue;
    }

    if (ch === '"') {
      inString = true;
      continue;
    }
    if (ch === "{") depth++;
    if (ch === "}") depth--;

    if (depth === 0) {
      return s.slice(start, i + 1);
    }
  }
  return null;
}

type GatewayDelta = {
  choices?: Array<{
    delta?: {
      content?: string;
    };
  }>;
};

async function readSSEToText(response: Response): Promise<string> {
  if (!response.body) return "";

  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  let textBuffer = "";
  let fullText = "";
  let streamDone = false;

  while (!streamDone) {
    const { done, value } = await reader.read();
    if (done) break;

    textBuffer += decoder.decode(value, { stream: true });

    let newlineIndex: number;
    while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
      let line = textBuffer.slice(0, newlineIndex);
      textBuffer = textBuffer.slice(newlineIndex + 1);

      if (line.endsWith("\r")) line = line.slice(0, -1);
      if (!line || line.startsWith(":")) continue; // keepalive/comments
      if (!line.startsWith("data:")) continue;

      const jsonStr = line.slice(5).trim();
      if (!jsonStr) continue;
      if (jsonStr === "[DONE]") {
        streamDone = true;
        break;
      }

      try {
        const parsed = JSON.parse(jsonStr) as GatewayDelta;
        const content = parsed.choices?.[0]?.delta?.content;
        if (typeof content === "string") fullText += content;
      } catch {
        // JSON line got split across chunks; re-buffer and wait for more
        textBuffer = line + "\n" + textBuffer;
        break;
      }
    }
  }

  // Final flush for any remaining complete lines
  if (textBuffer.trim()) {
    for (let raw of textBuffer.split("\n")) {
      if (!raw) continue;
      if (raw.endsWith("\r")) raw = raw.slice(0, -1);
      if (raw.startsWith(":")) continue;
      if (!raw.startsWith("data:")) continue;
      const jsonStr = raw.slice(5).trim();
      if (!jsonStr || jsonStr === "[DONE]") continue;
      try {
        const parsed = JSON.parse(jsonStr) as GatewayDelta;
        const content = parsed.choices?.[0]?.delta?.content;
        if (typeof content === "string") fullText += content;
      } catch {
        // ignore partial leftovers
      }
    }
  }

  return fullText;
}

// Helper function outside hook - not a hook, doesn't affect hook count
function parseStructuredResponse(rawResponse: string): string {
  try {
    const jsonStr = extractFirstJsonObject(rawResponse);
    if (!jsonStr) return rawResponse;

    const parsed = JSON.parse(jsonStr) as {
      directAnswer?: unknown;
      explanation?: unknown;
      recommendedAction?: unknown;
    };

    const directAnswer = typeof parsed.directAnswer === "string" ? parsed.directAnswer.trim() : "";
    const explanation = typeof parsed.explanation === "string" ? parsed.explanation.trim() : "";
    const recommendedAction = typeof parsed.recommendedAction === "string" ? parsed.recommendedAction.trim() : "";

    if (!directAnswer) return rawResponse;

    let result = directAnswer;
    if (explanation) result += (result.endsWith(".") ? " " : ". ") + explanation;
    if (recommendedAction) result += (result.endsWith(".") ? " " : ". ") + recommendedAction;
    return result;
  } catch {
    // Parsing failed, return original
  }
  return rawResponse;
}

export function useVoiceInterface(options: UseVoiceInterfaceOptions = {}) {
  const { session } = useAuth();
  const { toast } = useToast();

  // === ALL useState hooks FIRST ===
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [responseText, setResponseText] = useState("");
  const [hasPendingAudio, setHasPendingAudio] = useState(false);

  // === ALL useRef hooks SECOND ===
  const sessionRef = useRef(session);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const preloadedAudioRef = useRef<HTMLAudioElement | null>(null);
  const optionsRef = useRef(options);
  const pendingAudioUrlRef = useRef<string | null>(null);

  // Constants (not hooks)
  const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
  const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

  // === ALL useEffect hooks THIRD ===
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  // === ALL useCallback hooks FOURTH (in consistent order) ===
  
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    const token = sessionRef.current?.access_token;
    if (token) return token;
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? null;
  }, []);

  const initAudioContext = useCallback(() => {
    if (!preloadedAudioRef.current) {
      const audio = new Audio();
      audio.src = "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA";
      audio.volume = 0.01;
      audio.play().then(() => {
        audio.pause();
        audio.currentTime = 0;
        audio.volume = 1;
        preloadedAudioRef.current = audio;
        console.log("Audio context initialized for mobile");
      }).catch(() => {
        // Silent fail
      });
    }
  }, []);

  const playPendingAudio = useCallback(async () => {
    // Must be called from a user gesture (tap/click)
    if (!audioRef.current || !pendingAudioUrlRef.current) {
      setHasPendingAudio(false);
      return;
    }

    try {
      initAudioContext();
      setIsSpeaking(true);
      try {
        audioRef.current.currentTime = 0;
      } catch {
        // ignore
      }
      await audioRef.current.play();
      console.log("Playing pending audio");
      setHasPendingAudio(false);
    } catch (e) {
      console.error("Play pending audio error:", e);
      setIsSpeaking(false);
      setHasPendingAudio(true);
      toast({
        title: "Não foi possível tocar o áudio",
        description: "Verifique se o dispositivo não está no modo silencioso e tente novamente.",
        variant: "destructive",
      });
    }
  }, [toast, initAudioContext]);

  const speakText = useCallback(async (text: string) => {
    if (!text) return;

    const accessToken = await getAccessToken();
    if (!accessToken) {
      toast({
        title: "Sessão expirada",
        description: "Por favor, faça login novamente.",
        variant: "destructive",
      });
      return;
    }

    setIsSpeaking(true);

    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/elevenlabs-tts`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: SUPABASE_KEY,
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ text }),
        }
      );

      if (!response.ok) {
        throw new Error("Erro ao gerar áudio");
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);

       // Revoke previous pending audio URL if any
       if (pendingAudioUrlRef.current) {
         try {
           URL.revokeObjectURL(pendingAudioUrlRef.current);
         } catch {
           // ignore
         }
         pendingAudioUrlRef.current = null;
       }

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      let audio: HTMLAudioElement;
      if (preloadedAudioRef.current) {
        audio = preloadedAudioRef.current;
        audio.src = audioUrl;
      } else {
        audio = new Audio(audioUrl);
      }

      // iOS/Safari playback quirks
      audio.preload = "auto";
      audio.muted = false;
      audio.volume = 1;
      (audio as any).playsInline = true;
      
      audioRef.current = audio;
      pendingAudioUrlRef.current = audioUrl;

      audio.onended = () => {
        setIsSpeaking(false);
        setHasPendingAudio(false);
        URL.revokeObjectURL(audioUrl);
        if (pendingAudioUrlRef.current === audioUrl) {
          pendingAudioUrlRef.current = null;
        }
      };

      audio.onerror = () => {
        setIsSpeaking(false);
        // Keep as pending so user can retry, unless the resource is actually broken
        setHasPendingAudio(true);
        URL.revokeObjectURL(audioUrl);
        if (pendingAudioUrlRef.current === audioUrl) {
          pendingAudioUrlRef.current = null;
        }
      };

      try {
        await audio.play();
        console.log("Playing audio response");
        setHasPendingAudio(false);
      } catch (playError) {
        console.error("Play error:", playError);
        setIsSpeaking(false);
        setHasPendingAudio(true);
        toast({
          title: "Áudio disponível",
          description: "Toque no microfone novamente para ouvir a resposta.",
        });
      }
    } catch (error) {
      console.error("TTS error:", error);
      setIsSpeaking(false);
      setHasPendingAudio(false);
      toast({
        title: "Erro ao reproduzir áudio",
        description: "Não foi possível gerar a resposta por voz.",
        variant: "destructive",
      });
    }
  }, [getAccessToken, toast, SUPABASE_URL, SUPABASE_KEY]);

  const processAudio = useCallback(async (audioBlob: Blob) => {
    const accessToken = await getAccessToken();
    if (!accessToken) {
      toast({
        title: "Sessão expirada",
        description: "Por favor, faça login novamente.",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);

    try {
      console.log("Transcribing audio with Whisper STT...");
      const formData = new FormData();
      formData.append("audio", audioBlob, "recording.webm");

      // Tentar primeiro com Whisper (via Lovable AI), fallback para ElevenLabs
      let transcribedText = "";
      let sttSuccess = false;

      // Whisper STT via Lovable AI
      try {
        const whisperResponse = await fetch(
          `${SUPABASE_URL}/functions/v1/whisper-stt`,
          {
            method: "POST",
            headers: {
              apikey: SUPABASE_KEY,
              Authorization: `Bearer ${accessToken}`,
            },
            body: formData,
          }
        );

        if (whisperResponse.ok) {
          const whisperData = await whisperResponse.json();
          if (whisperData.text && whisperData.text.trim().length > 0) {
            transcribedText = whisperData.text;
            sttSuccess = true;
            console.log("Whisper transcription:", transcribedText);
          }
        }
      } catch (whisperError) {
        console.log("Whisper STT failed, falling back to ElevenLabs:", whisperError);
      }

      // Fallback para ElevenLabs STT
      if (!sttSuccess) {
        console.log("Using ElevenLabs STT fallback...");
        const sttResponse = await fetch(
          `${SUPABASE_URL}/functions/v1/elevenlabs-stt`,
          {
            method: "POST",
            headers: {
              apikey: SUPABASE_KEY,
              Authorization: `Bearer ${accessToken}`,
            },
            body: formData,
          }
        );

        if (!sttResponse.ok) {
          const error = await sttResponse.json();
          throw new Error(error.error || "Erro ao transcrever áudio");
        }

        const sttData = await sttResponse.json();
        transcribedText = sttData.text || "";
        console.log("ElevenLabs transcription:", transcribedText);
      }

      if (!transcribedText || transcribedText.trim().length === 0) {
        toast({
          title: "Não entendi",
          description: "Por favor, tente falar mais claramente.",
          variant: "destructive",
        });
        setIsProcessing(false);
        return;
      }

      setTranscript(transcribedText);
      optionsRef.current.onTranscription?.(transcribedText);

      console.log("Getting Sentinela response...");
      const chatResponse = await fetch(
        `${SUPABASE_URL}/functions/v1/sentinela-chat`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: SUPABASE_KEY,
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            messages: [{ role: "user", content: transcribedText }],
          }),
        }
      );

      if (!chatResponse.ok) {
        throw new Error("Erro ao obter resposta do Sentinela");
      }

      const fullResponse = await readSSEToText(chatResponse);

      console.log("Sentinela response received");

      const spokenText = parseStructuredResponse(fullResponse);

      setResponseText(spokenText);
      optionsRef.current.onResponse?.(spokenText);

      console.log("Converting to speech...");
      await speakText(spokenText);
    } catch (error) {
      console.error("Voice processing error:", error);
      toast({
        title: "Erro no processamento",
        description: error instanceof Error ? error.message : "Tente novamente",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  }, [getAccessToken, toast, speakText, SUPABASE_URL, SUPABASE_KEY]);

  const startListening = useCallback(async () => {
    initAudioContext();
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      streamRef.current = stream;
      audioChunksRef.current = [];

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : "audio/webm",
      });

      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;

        if (audioChunksRef.current.length === 0) {
          console.log("No audio recorded");
          return;
        }

        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });
        
        console.log("Audio recorded, size:", audioBlob.size);
        
        await processAudio(audioBlob);
      };

      mediaRecorder.start();
      setIsListening(true);
      setTranscript("");
      setResponseText("");

      console.log("Started listening...");
    } catch (error) {
      console.error("Error starting voice recording:", error);
      toast({
        title: "Erro ao acessar microfone",
        description: "Por favor, permita o acesso ao microfone.",
        variant: "destructive",
      });
    }
  }, [toast, initAudioContext, processAudio]);

  const stopListening = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
      setIsListening(false);
      console.log("Stopped listening");
    }
  }, []);

  const stopSpeaking = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setIsSpeaking(false);
    }

    if (pendingAudioUrlRef.current) {
      try {
        URL.revokeObjectURL(pendingAudioUrlRef.current);
      } catch {
        // ignore
      }
      pendingAudioUrlRef.current = null;
    }
    setHasPendingAudio(false);
  }, []);

  const cancel = useCallback(() => {
    stopListening();
    stopSpeaking();
    setIsProcessing(false);
    setTranscript("");
    setResponseText("");
  }, [stopListening, stopSpeaking]);

  return {
    isListening,
    isProcessing,
    isSpeaking,
    transcript,
    responseText,
    startListening,
    stopListening,
    stopSpeaking,
    speakText,
    hasPendingAudio,
    playPendingAudio,
    cancel,
  };
}
